import { Dispatch } from 'redux'
import getGuests from '../../axios/pms/guests/get'
import { Guest, GuestsQuery } from '../../axios/pms/guests/types'
import getChargeSessions from '../../axios/pms/chargeSessions/get'
import { cancelChargeSession, createCharge } from '../../axios/pms/chargeSessions/post'
import { ChargeSession, ChargeSubmission } from '../../axios/pms/chargeSessions/types'
import {
	setRoomChargeErrorCode,
	setRoomChargeGuests,
	setRoomChargeGuestsAreLoading,
	setRoomChargeIsProcessing,
} from '../../action/roomCharge'
import { RoomChargeOutcome } from '../../../component/RoomCharge/types'
import { logger } from '../../../logger/logger'

const CHARGEABLE_STATUS = 'CREATED'
const POSTED_STATUS = 'SUCCEEDED'
const POLL_INTERVAL_MS = 1000
const POLL_ATTEMPTS = 15
const OUTCOME_UNKNOWN_STATUS = 504
const CANCEL_UNAVAILABLE_STATUS = 503

type ApiErrorResponse = { response?: { status?: number; data?: { apierror?: { errorStatusCode?: string } } } }

const readStatus = (error: unknown): number | undefined => (error as ApiErrorResponse)?.response?.status

const readErrorCode = (error: unknown): string =>
	(error as ApiErrorResponse)?.response?.data?.apierror?.errorStatusCode ?? 'pms_unavailable'

const wait = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms))

const awaitChargeableSession = async (orderId: string): Promise<ChargeSession | null> => {
	for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt++) {
		const { data } = await getChargeSessions(orderId)
		const sessions: ChargeSession[] = data ?? []
		const posted = sessions.find(session => session.status === POSTED_STATUS)
		if (posted) return posted
		const chargeable = sessions.find(session => session.status === CHARGEABLE_STATUS)
		if (chargeable) return chargeable
		await wait(POLL_INTERVAL_MS)
	}
	return null
}

export const lookupRoomChargeGuestsThunk = (params: GuestsQuery) => async (dispatch: Dispatch): Promise<Guest[]> => {
	dispatch(setRoomChargeGuestsAreLoading(true))
	dispatch(setRoomChargeErrorCode(null))
	try {
		const { data } = await getGuests(params)
		const guests: Guest[] = data?.guests ?? []
		dispatch(setRoomChargeGuests(guests))
		return guests
	} catch (error) {
		dispatch(setRoomChargeErrorCode(readErrorCode(error)))
		dispatch(setRoomChargeGuests([]))
		return []
	} finally {
		dispatch(setRoomChargeGuestsAreLoading(false))
	}
}

export const submitRoomChargeThunk = (orderId: string, submission: ChargeSubmission) =>
	async (dispatch: Dispatch): Promise<RoomChargeOutcome> => {
		dispatch(setRoomChargeIsProcessing(true))
		dispatch(setRoomChargeErrorCode(null))
		try {
			const session = await awaitChargeableSession(orderId)
			if (!session) {
				dispatch(setRoomChargeErrorCode('timeout'))
				return { status: 'TIMEOUT' }
			}
			if (session.status === POSTED_STATUS) {
				return { status: 'POSTED', session }
			}
			const { data } = await createCharge(session.id, submission)
			return { status: 'POSTED', session: data }
		} catch (error) {
			if (readStatus(error) === OUTCOME_UNKNOWN_STATUS) {
				dispatch(setRoomChargeErrorCode('charge_outcome_unknown'))
				return { status: 'UNKNOWN' }
			}
			const code = readErrorCode(error)
			dispatch(setRoomChargeErrorCode(code))
			return { status: 'REJECTED', code }
		} finally {
			dispatch(setRoomChargeIsProcessing(false))
		}
	}

export const cancelRoomChargeThunk = (orderId: string) => async (): Promise<{ unavailable: boolean }> => {
	try {
		const { data } = await getChargeSessions(orderId)
		const session = (data ?? []).find((entry: ChargeSession) => entry.status === CHARGEABLE_STATUS)
		if (session) await cancelChargeSession(session.id)
		return { unavailable: false }
	} catch (error) {
		if (readStatus(error) === CANCEL_UNAVAILABLE_STATUS) return { unavailable: true }
		logger.error(`Room charge session of order ${orderId} could not be cancelled: ${(error as Error)?.message}`)
		return { unavailable: false }
	}
}
